import * as React from "react";
import {Button, Control, Field, Icon, Input, Label} from "bloomer";
import {Container, FormContainer, Header, Switch} from "./Styles";
import {BACKEND_URI} from "../../utils/Constants";
import styled from "react-emotion";
import {EMAIL_REGEX} from "../../utils/Constants";
import Spacer from "../../elements/Spacer";
import FBPixelPageView from "../../utils/FBPixelPageView";

const FieldErrorMessage = styled("div")`
  width: 230px;
  text-align: left;
  font-size: 14px;
  color: red;
`;

const ErrorMessage = styled("div")`
  text-align: center;
  color: red;
`;

class Register extends React.Component {
  public props: {
    switch(value: string): void;
  };

  public NAME_REGEX = new RegExp('^[a-zA-Z\\s]+$');
  public NAME_MAX_LENGTH = 30;
  public PASSWORD_MIN_LENGTH = 6;
  public PASSWORD_MAX_LENGTH = 30;
  public SPECIAL_CHARACTERS = '!"#$%&()*+-.:;<=>?@[]^_{|}~';

  public state = {
    loading: false,
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordConfirmation: "",
    errorMessages: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      passwordConfirmation: ""
    },
    errorMessage: "",
    responseError: false
  };

  public componentDidMount() {
    FBPixelPageView();
  }

  public containsUpperCase = (str: string) => {
    return str.toLowerCase() !== str;
  };

  public containsSpecialCharacter = (str: string) => {
    return this.SPECIAL_CHARACTERS.split('').map((c: string) => {
      return str.indexOf(c) !== -1;
    }).filter(b => b).length > 0;
  };

  public validateNameField = (value: string, field: string) => {
    if (!value) {
      return "Please enter " + field + ".";
    } else if (value.length > this.NAME_MAX_LENGTH) {
      return "Maximum number of characters exceeded.";
    } else if (!this.NAME_REGEX.test(value)) {
      return "Use English letters.";
    }
    return "";
  };

  public validateEmail = (value: any) => {
    if (!value) {
      return "Please enter email.";
    } else if (!EMAIL_REGEX.test(value)) {
      return "Please enter valid email.";
    }
    return "";
  };

  public validatePassword = (password: string) => {
    if (!password) {
      return "Please enter password.";
    }
    if (password.length < this.PASSWORD_MIN_LENGTH) {
      return "Password is too short.";
    }
    if (password.length > this.PASSWORD_MAX_LENGTH) {
      return "Password is too long.";
    }
    if (!this.containsUpperCase(password)) {
      return "Password must contain an uppercase character.";
    }
    if (!this.containsSpecialCharacter(password)) {
      return "Password must contain a special character.";
    }
    return "";
  };

  public validatePasswordConfirmation = (password: string, passwordConfirmation: string) => {
    if (password !== passwordConfirmation) {
      return "Passwords do not match.";
    }
    return "";
  };

  public handleFirstNameChange = (e: any) => {
    const firstName = e.target.value;
    const errorMessages = this.state.errorMessages;
    errorMessages.firstName = this.validateNameField(firstName.trim(), " first name");
    this.setState({
      firstName,
      errorMessages
    });
  };

  public handleLastNameChange = (e: any) => {
    const lastName = e.target.value;
    const errorMessages = this.state.errorMessages;
    errorMessages.lastName = this.validateNameField(lastName.trim(), " last name");
    this.setState({
      lastName,
      errorMessages
    });
  };

  public handleEmailChange = (e: any) => {
    const email = e.target.value;
    const errorMessages = this.state.errorMessages;
    errorMessages.email = this.validateEmail(email.trim());
    this.setState({
      email,
      errorMessages
    });
  };

  public handlePasswordChange = (e: any) => {
    const password = e.target.value;
    const errorMessages = this.state.errorMessages;
    errorMessages.password = this.validatePassword(password.trim());
    this.setState({
      password,
      errorMessages
    });
  };

  public handlePasswordConfirmationChange = (e: any) => {
    const passwordConfirmation = e.target.value;
    const errorMessages = this.state.errorMessages;
    errorMessages.passwordConfirmation = this.validatePasswordConfirmation(this.state.password.trim(), passwordConfirmation);
    this.setState({
      passwordConfirmation,
      errorMessages
    });
  };

  public handleRegistrationResponse = (data: any) => {
    if (data.email !== null && data.email !== undefined) {
      this.props.switch("registered");
    } else {
      this.setState({ responseError: true, responseOk: false });
    }
  };

  public resetErrors = (callback: any) => {
    this.setState({
      errorMessages: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        passwordConfirmation: ""
      },
      errorMessage: ""
    }, () => {
      callback();
    });
  };

  public handleSubmit = () => {
    if (this.state.loading) {
      return;
    }

    const { firstName, lastName, email, password, passwordConfirmation } = this.state;

    // check if all fields are empty
    const allFields = [firstName, lastName, email, password, passwordConfirmation];
    if (allFields.map(f => f.trim()).filter(f => f.length > 0).length === 0) {
      this.setState({errorMessage: "Please fill out the information and then click submit."});
      return;
    }

    const firstNameMsg = this.validateNameField(this.state.firstName.trim(), "first name");
    const lastNameMsg = this.validateNameField(this.state.lastName.trim(), "last name");
    const emailErrorMsg = this.validateEmail(this.state.email.trim());
    const passwordErrorMsg = this.validatePassword(this.state.password.trim());
    const passwordConfirmationMsg = this.validatePasswordConfirmation(this.state.password.trim(), this.state.passwordConfirmation);

    if (firstNameMsg || lastNameMsg || emailErrorMsg || passwordErrorMsg || passwordConfirmationMsg) {
      this.setState({
        errorMessages: {
          firstName: firstNameMsg,
          lastName: lastNameMsg,
          email: emailErrorMsg,
          password: passwordErrorMsg,
          passwordConfirmation: passwordConfirmationMsg
        }
      });
      return;
    }

    this.setState({
      errorMessage: "",
      loading: true
    });

    fetch(`${BACKEND_URI}/auth/signup`, {
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        email,
        password
      }),
      headers: {
        "Content-Type": "application/json"
      },
      method: "POST"
    }).then((response) => {
      response.json().then((result) => {
        if (response.status === 400 && result.message) {
          this.setState({
            errorMessage: result.message,
            responseError: false,
            loading: false
          });
        } else {
          this.handleRegistrationResponse(result);
        }
      });
    }).catch(() => {
      this.setState({
        responseError: true,
        loading: false
      });
    });
  };

  public render() {
    return (
        <Container>
          <Header>Sign Up</Header>
          <FormContainer>
            <Field>
              <Label>First Name</Label>
              <Control>
                <Input
                    type="text"
                    placeholder="Enter your first name"
                    value={this.state.firstName}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {this.handleFirstNameChange(e);}}
                />
              </Control>
              {
                this.state.errorMessages.firstName ? <FieldErrorMessage>{this.state.errorMessages.firstName}</FieldErrorMessage> : <></>
              }
            </Field>

            <Field>
              <Label>Last Name</Label>
              <Control>
                <Input
                    type="text"
                    placeholder="Enter your last name"
                    value={this.state.lastName}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {this.handleLastNameChange(e);}}
                />
              </Control>
              {
                this.state.errorMessages.lastName ? <FieldErrorMessage>{this.state.errorMessages.lastName}</FieldErrorMessage> : <></>
              }
            </Field>

            <Field>
              <Label>E-mail</Label>
              <Control>
                <Input
                    type="text"
                    placeholder="Enter your e-mail"
                    value={this.state.email}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {this.handleEmailChange(e);}}
                />
              </Control>
              {
                this.state.errorMessages.email ? <FieldErrorMessage>{this.state.errorMessages.email}</FieldErrorMessage> : <></>
              }
            </Field>

            <Field>
              <Label>Password</Label>
              <Control>
                <Input
                    type="password"
                    placeholder="Enter your password"
                    value={this.state.password}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {this.handlePasswordChange(e);}}
                />
              </Control>
              {
                this.state.errorMessages.password ? <FieldErrorMessage>{this.state.errorMessages.password}</FieldErrorMessage> : <></>
              }
            </Field>

            <Field>
              <Label>Password Confirmation</Label>
              <Control>
                <Input
                    type="password"
                    placeholder="Confirm your password"
                    value={this.state.passwordConfirmation}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {
                      this.handlePasswordConfirmationChange(e);
                    }}
                />
              </Control>
              {
                this.state.errorMessages.passwordConfirmation ? <FieldErrorMessage>{this.state.errorMessages.passwordConfirmation}</FieldErrorMessage> : <></>
              }
            </Field>
          </FormContainer>
          {
            this.state.loading ? <div>
              <span>Loading...</span>
              <Icon className="fas fa-spinner fa-spin"/>
              <Spacer/>
            </div> : <Button
                style={{marginTop: '-10px', marginBottom: '10px'}}
                onClick={() => {this.resetErrors(this.handleSubmit);}}
            >Submit</Button>
          }
          {
            this.state.errorMessage ? <ErrorMessage>{this.state.errorMessage}</ErrorMessage> : <></>
          }
          {
            this.state.responseError ? <ErrorMessage>There was an error! Try again.</ErrorMessage> : <></>
          }
          <div>
            or{" "}
            <Switch onClick={() => this.props.switch("login")}>Sign In</Switch>{" "}
            instead
          </div>
          <noscript
              dangerouslySetInnerHTML={{
                  __html: `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=200009405644523&ev=PageView&noscript=1" />`
              }}
          />
        </Container>
    );
  }
}

export default Register;



// WEBPACK FOOTER //
// ./src/routes/Login/Register.tsx